import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spin, Tooltip } from 'antd'
import {
	ArrowLeft,
	Ban,
	ChevronDown,
	ChevronRight,
	Pencil,
	TriangleAlert,
} from 'lucide-react'
import DemoShell from './components/DemoShell'
import { Card, InfoItem } from './components/Field'
import { EmptyRow, TableWrap, Td, Th } from './components/Table'
import CashbackCard from './components/CashbackCard'
import {
	InstallmentStatusBadge,
	InvoiceStatusBadge,
	SaleTypeBadge,
} from './components/Badges'
import { getInstallments, getInvoiceByDocEntry, getPayments } from './api'
import { useSaleCashback } from './hooks'
import { DEMO_INVOICES } from './constants'
import {
	dash,
	formatDate,
	formatMoney,
	getApiErrorMessage,
	isDue,
	isOverdue,
	isUnauthorized,
	toNumber,
	udf,
} from './utils'

const LINE_COLSPAN = 11

// Eskisidagi «У кого этот продукт?» maydoni
const FREEZED_LABELS = { Y: 'Olib qo‘yilgan', N: 'Mijozda' }

const ExtraField = ({ label, value }) => (
	<div className="flex flex-col gap-0.5">
		<span className="text-[10px] uppercase tracking-wide text-slate-400">
			{label}
		</span>
		<span className="text-[13px] text-slate-700">{dash(value)}</span>
	</div>
)

// Eskisidagi yuqoridagi kartochka: Курс / Долг / Общий долг plitkalari
const Stat = ({ label, value, tone = 'slate', hint }) => {
	const tones = {
		slate: 'bg-slate-50 text-slate-900',
		emerald: 'bg-emerald-50 text-emerald-700',
		amber: 'bg-amber-50 text-amber-700',
		rose: 'bg-rose-50 text-rose-700',
	}
	return (
		<div className={`rounded-xl p-3 ${tones[tone]}`}>
			<p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
			<p className="mt-1 text-lg font-semibold">{value}</p>
			{hint && <p className="mt-0.5 text-[11px] opacity-70">{hint}</p>}
		</div>
	)
}

const InvoiceCard = () => {
	const { docEntry } = useParams()
	const navigate = useNavigate()
	const paymentsRef = useRef(null)

	const [invoice, setInvoice] = useState(null)
	const [installments, setInstallments] = useState([])
	const [payments, setPayments] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [expandedLines, setExpandedLines] = useState(() => new Set())
	const [highlightedPayment, setHighlightedPayment] = useState(null)

	useEffect(() => {
		const controller = new AbortController()
		const config = { signal: controller.signal }
		setLoading(true)
		setError('')

		// TZ 4: uchta so'rov parallel yuboriladi
		Promise.all([
			getInvoiceByDocEntry(docEntry, config),
			getInstallments(docEntry, config).catch(() => []),
			getPayments(docEntry, config).catch(() => []),
		])
			.then(([invoiceData, installmentsData, paymentsData]) => {
				if (controller.signal.aborted) return
				setInvoice(invoiceData)
				setInstallments(installmentsData)
				setPayments(paymentsData)
				if (!invoiceData) setError('Hujjat topilmadi')
			})
			.catch((err) => {
				if (controller.signal.aborted) return
				if (isUnauthorized(err)) {
					navigate('/login', { replace: true })
					return
				}
				setError(getApiErrorMessage(err))
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false)
			})

		return () => controller.abort()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docEntry])

	// Eskisidagi «Кэшбэк по продаже» — telefon bo'yicha leder + SAP sverkasi
	const cashback = useSaleCashback({
		docEntry,
		phone: invoice?.customerPhoneRaw || invoice?.customerPhone || '',
		cardCode: invoice?.cardCode || '',
	})

	const toggleLine = (lineKey) =>
		setExpandedLines((prev) => {
			const next = new Set(prev)
			if (next.has(lineKey)) next.delete(lineKey)
			else next.add(lineKey)
			return next
		})

	const scrollToPayment = useCallback((paymentDocNum) => {
		if (!paymentDocNum) return
		setHighlightedPayment(paymentDocNum)
		const row = paymentsRef.current?.querySelector(
			`[data-payment-doc="${paymentDocNum}"]`,
		)
		row?.scrollIntoView({ behavior: 'smooth', block: 'center' })
		window.setTimeout(() => setHighlightedPayment(null), 2500)
	}, [])

	const lines = invoice?.documentLines || []
	const isCredit = invoice?.saleType === 'credit'
	const installmentLines = useMemo(
		() => installments.flatMap((item) => item.documentLines || []),
		[installments],
	)

	// Eskisidagi «Долг»: muddati kelib bo'lgan oylar bo'yicha qarz
	// (kelajakdagi to'lovlar hisobga olinmaydi)
	const dueDebt = useMemo(
		() =>
			installmentLines
				.filter((row) => isDue(row.dueDate))
				.reduce((sum, row) => sum + toNumber(row.openAmount), 0),
		[installmentLines],
	)

	const overdueCount = useMemo(
		() =>
			installmentLines.filter((row) => isOverdue(row.dueDate, row.openAmount))
				.length,
		[installmentLines],
	)

	const paidPercent = useMemo(() => {
		const total = toNumber(invoice?.docTotal)
		if (!total) return 0
		return Math.min(100, Math.round((toNumber(invoice?.paidToDate) / total) * 100))
	}, [invoice])

	if (loading) {
		return (
			<DemoShell title="Hujjat" subtitle="Yuklanmoqda…">
				<div className="flex min-h-[50vh] items-center justify-center">
					<Spin size="large" />
				</div>
			</DemoShell>
		)
	}

	if (error || !invoice) {
		return (
			<DemoShell title="Hujjat">
				<div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
					<TriangleAlert size={18} className="mt-0.5 shrink-0" />
					<div>
						<p className="font-medium">{error || 'Hujjat topilmadi'}</p>
						<button
							type="button"
							onClick={() => navigate(DEMO_INVOICES)}
							className="mt-2 text-[13px] font-medium underline"
						>
							Ro‘yxatga qaytish
						</button>
					</div>
				</div>
			</DemoShell>
		)
	}

	return (
		<DemoShell
			title={`Hujjat №${invoice.docNum}`}
			subtitle={`docEntry: ${invoice.docEntry}`}
			actions={
				/* TZ 6: PATCH va cancel API lari hali yo'q — tugmalar disabled */
				<div className="flex items-center gap-2">
					<Tooltip title="Backendda PATCH /api/invoices/{docEntry} hali qo‘shilmagan">
						<span>
							<button
								type="button"
								disabled
								className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-400"
							>
								<Pencil size={15} /> Tahrirlash
							</button>
						</span>
					</Tooltip>
					<Tooltip title="Backendda POST /api/invoices/{docEntry}/cancel hali qo‘shilmagan">
						<span>
							<button
								type="button"
								disabled
								className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-400"
							>
								<Ban size={15} /> Bekor qilish
							</button>
						</span>
					</Tooltip>
				</div>
			}
		>
			<div className="mb-4 flex flex-wrap items-center gap-2">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="flex h-8 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
				>
					<ArrowLeft size={15} /> Orqaga
				</button>
				<InvoiceStatusBadge invoice={invoice} />
				<SaleTypeBadge
					saleType={invoice.saleType}
					installments={invoice.installments}
				/>
				{overdueCount > 0 && (
					<span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-[12px] font-medium text-rose-700">
						<TriangleAlert size={13} />
						{`Muddati o‘tgan: ${overdueCount} ta`}
					</span>
				)}
			</div>

			<div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
				{/* Eskisidagi yuqori qator: ФИО · Гарант · Кому принадлежит · Менеджер · Аккаунт защиты */}
				<Card title="Mijoz va savdo" className="lg:col-span-2">
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
						<InfoItem label="Mijoz (FIO)" value={dash(invoice.cardName)} />
						<InfoItem label="Mijoz kodi" value={dash(invoice.cardCode)} />
						<InfoItem
							label="Telefon"
							value={
								<Tooltip title={invoice.customerPhoneRaw || ''}>
									<span>{dash(invoice.customerPhone)}</span>
								</Tooltip>
							}
						/>
						<InfoItem
							label="Kafil (Garant)"
							value={dash(udf(invoice, 'U_Guarantor'))}
						/>
						<InfoItem
							label="Telefon kimga tegishli"
							value={dash(udf(invoice, 'U_Owner'))}
						/>
						<InfoItem
							label="Sotuvchi (menejer)"
							value={dash(invoice.slpName || invoice.slpCode)}
						/>
						<InfoItem
							label="Himoya akkaunti (iCloud)"
							value={dash(udf(invoice, 'U_Icloud'))}
						/>
						<InfoItem label="Hujjat sanasi" value={formatDate(invoice.docDate)} />
						<InfoItem
							label="To‘lov muddati"
							value={formatDate(invoice.docDueDate)}
						/>
						<InfoItem
							label="Savdo turi (UDF)"
							value={dash(udf(invoice, 'U_TypeInvoice'))}
						/>
						<InfoItem
							label="Installmentlar soni"
							value={dash(invoice.installments)}
						/>
						<InfoItem
							label="Boshlang‘ich to‘lov"
							value={formatMoney(invoice.firstPayment)}
						/>
						<InfoItem
							label="Status (U_Score)"
							value={dash(udf(invoice, 'U_Score'))}
						/>
						<InfoItem
							label="Mahsulot kimda"
							value={dash(
								FREEZED_LABELS[udf(invoice, 'U_IsFreezed')] ??
									udf(invoice, 'U_IsFreezed'),
							)}
						/>
						<InfoItem
							label="Undiruvchi"
							value={dash(udf(invoice, 'U_Undrkim'))}
						/>
						<InfoItem
							label="Xodim (U_EmployeeId)"
							value={dash(udf(invoice, 'U_EmployeeId'))}
						/>
						<InfoItem
							label="Hujjat holati"
							value={invoice.docStatus === 'C' ? 'Yopiq' : 'Ochiq'}
						/>
						<InfoItem
							label="Izoh"
							value={dash(invoice.comments)}
							className="col-span-2 sm:col-span-3"
						/>
					</div>
				</Card>

				{/* Eskisidagi «Долг / Общий долг» plitkalari */}
				<Card title="Summalar">
					<div className="flex flex-col gap-3">
						<div className="rounded-xl bg-slate-50 p-3">
							<p className="text-[11px] uppercase tracking-wide text-slate-400">
								Hujjat summasi
							</p>
							<p className="mt-1 text-2xl font-semibold text-slate-900">
								{formatMoney(invoice.docTotal)}
							</p>
							<div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
								<div
									className="h-full rounded-full bg-emerald-500"
									style={{ width: `${paidPercent}%` }}
								/>
							</div>
							<p className="mt-1 text-[11px] text-slate-400">
								{`To‘langani — ${paidPercent}%`}
							</p>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<Stat
								label="To‘langan"
								tone="emerald"
								value={formatMoney(invoice.paidToDate)}
							/>
							<Stat
								label="Qolgan qarz"
								tone="amber"
								value={formatMoney(invoice.openBalance)}
							/>
						</div>
						{isCredit && (
							<Stat
								label="Muddati kelgan qarz"
								tone={dueDebt > 0 ? 'rose' : 'slate'}
								value={formatMoney(dueDebt, { fallback: '0' })}
								hint="Bugungi sanaga qadar to‘lanishi kerak bo‘lgan, lekin yopilmagan qism"
							/>
						)}
					</div>
				</Card>
			</div>

			{/* Eskisidagi tovar jadvali: Товар · Серийный номер · Цвет · Состояние ·
			    Цена витрина · Цена продажи */}
			<Card
				title="Tovarlar"
				subtitle="Qatorni ochib qo‘shimcha maydonlarni ko‘rish mumkin"
				className="mb-5"
			>
				<TableWrap minWidth="min-w-[1180px]">
					<thead>
						<tr>
							<Th className="w-10" />
							<Th>Tovar</Th>
							<Th>Sotilgan IMEI</Th>
							<Th>Rangi</Th>
							<Th>Holati</Th>
							<Th align="right">Batareya</Th>
							<Th align="right">Miqdor</Th>
							<Th align="right">Vitrina narxi</Th>
							<Th align="right">Narx</Th>
							<Th align="right">Summa</Th>
							<Th>Ombor</Th>
						</tr>
					</thead>
					<tbody>
						{lines.length === 0 && (
							<EmptyRow colSpan={LINE_COLSPAN} text="Tovar qatorlari yo‘q" />
						)}
						{lines.map((line, index) => {
							const lineKey = `${line.itemCode}-${line.lineNum ?? index}`
							const expanded = expandedLines.has(lineKey)
							const battery = udf(line, 'U_Percent')
							return (
								<React.Fragment key={lineKey}>
									<tr
										className="cursor-pointer transition hover:bg-slate-50"
										onClick={() => toggleLine(lineKey)}
									>
										<Td className="text-slate-400">
											{expanded ? (
												<ChevronDown size={16} />
											) : (
												<ChevronRight size={16} />
											)}
										</Td>
										<Td>
											<p className="font-medium text-slate-800">
												{dash(line.itemName)}
											</p>
											<p className="text-[12px] text-slate-400">
												{dash(line.itemCode)}
											</p>
										</Td>
										<Td className="font-mono text-[13px]">
											{dash(line.soldIMEI)}
										</Td>
										<Td>{dash(udf(line, 'U_Color'))}</Td>
										<Td>{dash(udf(line, 'U_Condition'))}</Td>
										<Td className="text-right">
											{battery ? `${battery}%` : '—'}
										</Td>
										<Td className="text-right">{dash(line.quantity)}</Td>
										<Td className="whitespace-nowrap text-right text-slate-500">
											{formatMoney(line.shopfrontPrice)}
										</Td>
										<Td className="whitespace-nowrap text-right">
											{formatMoney(line.price)}
										</Td>
										<Td className="whitespace-nowrap text-right font-medium">
											{formatMoney(line.lineTotal)}
										</Td>
										<Td className="whitespace-nowrap">
											{dash(
												[line.warehouseCode, line.whsName]
													.filter(Boolean)
													.join(' · '),
											)}
										</Td>
									</tr>
									{expanded && (
										<tr>
											<td
												colSpan={LINE_COLSPAN}
												className="bg-slate-50/70 px-3 py-4"
											>
												<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
													<ExtraField
														label="Asl nomi"
														value={udf(line, 'U_OriginalName')}
													/>
													<ExtraField
														label="Sotib olingan IMEI"
														value={udf(line, 'U_IMEI_PURCH')}
													/>
													<ExtraField
														label="Karobkasi"
														value={udf(line, 'U_Box')}
													/>
													<ExtraField
														label="Izoh"
														value={udf(line, 'U_Primechaniye')}
													/>
													<ExtraField
														label="Region"
														value={udf(line, 'U_Region_Item')}
													/>
													<ExtraField
														label="Sys serial / AbsEntry"
														value={[line.sysSerial, line.serialAbsEntry]
															.filter((v) => v !== null && v !== undefined)
															.join(' / ')}
													/>
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							)
						})}
					</tbody>
				</TableWrap>
			</Card>

			{/* TZ 4.3 · eskisidagi «План Платежа» — faqat nasiyada */}
			{isCredit && (
				<Card
					title="To‘lov grafigi (План платежа)"
					subtitle="To‘lov hujjati raqamiga bosilsa — pastdagi to‘lovlar jadvaliga o‘tadi"
					className="mb-5"
				>
					<TableWrap minWidth="min-w-[820px]">
						<thead>
							<tr>
								<Th>№</Th>
								<Th>Muddati</Th>
								<Th align="right">Summasi</Th>
								<Th align="right">To‘langani</Th>
								<Th align="right">Qolgani</Th>
								<Th>Holati</Th>
								<Th>To‘lov hujjati</Th>
								<Th>To‘langan sana</Th>
							</tr>
						</thead>
						<tbody>
							{installmentLines.length === 0 && (
								<EmptyRow colSpan={8} text="Installmentlar topilmadi" />
							)}
							{installmentLines.map((row) => {
								const overdue = isOverdue(row.dueDate, row.openAmount)
								const closed = ['Closed', 'closed', 'C'].includes(row.status)
								return (
									<tr
										key={row.installmentId}
										className={
											closed
												? 'bg-emerald-50/40'
												: overdue
													? 'bg-rose-50/50'
													: ''
										}
									>
										<Td className="font-semibold text-slate-800">
											{row.installmentId}
										</Td>
										<Td
											className={`whitespace-nowrap ${
												overdue ? 'font-medium text-rose-600' : ''
											}`}
										>
											{formatDate(row.dueDate)}
										</Td>
										<Td className="whitespace-nowrap text-right font-medium">
											{formatMoney(row.insTotal)}
										</Td>
										<Td className="whitespace-nowrap text-right text-emerald-600">
											{formatMoney(row.paidToDate)}
										</Td>
										<Td className="whitespace-nowrap text-right">
											{formatMoney(row.openAmount)}
										</Td>
										<Td>
											<InstallmentStatusBadge
												status={row.status}
												overdue={overdue}
											/>
										</Td>
										<Td>
											{row.paymentDocNum ? (
												<button
													type="button"
													onClick={() => scrollToPayment(row.paymentDocNum)}
													className="font-medium text-indigo-600 underline-offset-2 hover:underline"
												>
													№{row.paymentDocNum}
												</button>
											) : (
												'—'
											)}
										</Td>
										<Td className="whitespace-nowrap">
											{formatDate(row.paidDate)}
										</Td>
									</tr>
								)
							})}
						</tbody>
					</TableWrap>
				</Card>
			)}

			{/* TZ 4.4 · eskisidagi «Факт Платежа» */}
			<Card
				title="To‘lovlar (Факт платежа)"
				subtitle="Asosiy summa — shu hujjatga tushgan qismi (appliedAmount)"
				className="mb-5"
			>
				<div ref={paymentsRef}>
					<TableWrap minWidth="min-w-[820px]">
						<thead>
							<tr>
								<Th>To‘lov №</Th>
								<Th>Sanasi</Th>
								<Th>Kassa / hisob</Th>
								<Th align="right">Shu hujjatga</Th>
								<Th align="right">To‘lov summasi</Th>
								<Th align="center">Installment</Th>
							</tr>
						</thead>
						<tbody>
							{payments.length === 0 && (
								<EmptyRow colSpan={6} text="To‘lovlar topilmadi" />
							)}
							{payments.map((payment, index) => (
								<tr
									key={`${payment.docEntry}-${payment.installmentId}-${index}`}
									data-payment-doc={payment.docNum}
									className={`transition ${
										highlightedPayment === payment.docNum
											? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
											: ''
									}`}
								>
									<Td className="font-semibold text-slate-800">
										№{payment.docNum}
									</Td>
									<Td className="whitespace-nowrap">
										{formatDate(payment.docDate)}
									</Td>
									<Td>
										<p className="text-slate-700">{dash(payment.acctName)}</p>
										<p className="text-[12px] text-slate-400">
											{dash(payment.cashAcct)}
										</p>
									</Td>
									<Td className="whitespace-nowrap text-right font-semibold text-slate-900">
										{formatMoney(payment.appliedAmount)}
									</Td>
									<Td className="whitespace-nowrap text-right text-slate-500">
										{formatMoney(payment.docTotal)}
									</Td>
									<Td className="text-center">{dash(payment.installmentId)}</Td>
								</tr>
							))}
						</tbody>
					</TableWrap>
				</div>
			</Card>

			<CashbackCard docEntry={invoice.docEntry} cashback={cashback} />
		</DemoShell>
	)
}

export default InvoiceCard
